'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import {
  ReactNode,
  createContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  MEMBERSHIP_PRODUCT_API_PROD,
  ProductPurchaseOption,
  ProductPurchaseType,
  productShopifyIdMap,
} from 'content/products';
import { NewM1Data } from 'content/products/m1/IN';
import { UhxShopifyMap } from 'content/products/uhx';
import { Currency } from 'utils/Currency';
import { useForwardProductParams } from 'hooks/navigation/useForwardProductParams';
import { useForwardSearchParamsShopify } from 'hooks/navigation/useForwardSearchParamsShopify';
import { useForwardTrackingParams } from 'hooks/navigation/useForwardTrackingParams';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from 'lib/commerce/shopify/shopifyHelper';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { addToCartArgsType, ProductType } from 'components/shared/Cart/types';
import { tagBundle } from 'components/shared/Cart/bundle';
import { SubscriptionDisclaimerDiv } from 'components/shared/styledComponents';
import { ButtonV2 } from 'components/shared/ButtonV2';
import {
  m1PromoDiscount,
  getCampaignPromoDiscountConfig,
} from 'content/referral';
import { getCampaignConfig } from 'content/campaigns';
import { isM1UhxBundlingEnabled } from 'content/products/m1/m1UhxBundlingConfig';
import { ultrahumanXDiscountCodes } from 'content/ultrahumanX';

type M1Context = {
  currency: Currency;
  store: ShopifyStore;
  locale?: string; // Country code (e.g., 'gb', 'de', 'us')
  productOption: ProductPurchaseOption;
  changeProductOption: (option: ProductPurchaseOption) => void;
  product: ProductPurchaseType;
  changeProduct: (product: ProductPurchaseType) => void;
  price: number;
  checkout: () => Promise<void>;
  products: NewM1Data;
  showMedicalDisclaimer: boolean;
  directCheckout: boolean;
  isLoading: boolean;
  shippingEstimate: string;
  effectivePrice: number;
  outOfStock?: boolean;

  mcfDeliveryEstimateState: boolean;
  setMcfDeliveryEstimateState: (value: boolean) => void;

  // Discount properties
  affiliateDiscount: number;
  birthdayDiscount: number;
  referralDiscount: number;
  campaignDiscount: number;
  effectiveDiscount: { type: string; amount: number };
  m1CampaignActive: boolean;
  campaignTitle?: string;
};

const initialM1SelectionContext: M1Context = {
  currency: Currency.inr,
  store: ShopifyStore.IN,
  locale: undefined,
  productOption: ProductPurchaseOption.oneTimePurchase,
  changeProductOption: () => null,
  product: ProductPurchaseType.otp_12_week,
  changeProduct: () => null,
  price: 0,
  checkout: async () => undefined,
  products: {},
  showMedicalDisclaimer: false,
  directCheckout: false,
  isLoading: false,
  shippingEstimate: '',
  effectivePrice: 0,
  outOfStock: false,

  mcfDeliveryEstimateState: false,
  setMcfDeliveryEstimateState: () => null,

  // Discount properties
  affiliateDiscount: 0,
  birthdayDiscount: 0,
  referralDiscount: 0,
  campaignDiscount: 0,
  effectiveDiscount: { type: 'affiliate', amount: 0 },
  m1CampaignActive: false,
  campaignTitle: undefined,
};

export const M1SelectionContext = createContext<M1Context>(
  initialM1SelectionContext,
);

export const M1SelectionProvider = ({
  children,
  currency,
  store,
  locale,
  products,
  defaultProductOption,
  defaultProduct,
  outOfStock,
  mcfDeliveryEstimate = false,
}: {
  children: ReactNode;
  currency: Currency;
  store: ShopifyStore;
  locale?: string;
  products: NewM1Data;
  defaultProductOption: ProductPurchaseOption;
  defaultProduct: ProductPurchaseType;
  outOfStock?: boolean;
  mcfDeliveryEstimate?: boolean;
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    addToCart,
    ultrahumanXAdded,
    affiliateDetails,
    campaignActive,
    cart,
    showCart,
    setAltContent,
    isCartVisible,
    workAffiliateDiscount,
    getCampaignForProduct,
  } = useCartStore(
    useShallow((s) => ({
      addToCart: s.addToCart,
      ultrahumanXAdded: s.ultrahumanXAdded,
      affiliateDetails: s.affiliateDetails,
      campaignActive: s.campaignActive,
      cart: s.cart,
      showCart: s.showCart,
      setAltContent: s.setAltContent,
      isCartVisible: s.isCartVisible,
      workAffiliateDiscount: s.workAffiliateDiscount,
      getCampaignForProduct: s.getCampaignForProduct,
    })),
  );

  const [mcfDeliveryEstimateState, setMcfDeliveryEstimateState] = useState(
    mcfDeliveryEstimate ?? false,
  );

  const [shippingEstimate, setShippingEstimate] = useState<string>('');

  const forwardSearchParams = useForwardSearchParamsShopify();

  const forwardProductParams = useForwardProductParams({
    defaultCurrency: currency,
  });
  const forwardTrackingParams = useForwardTrackingParams();
  const [isLoading, setIsLoading] = useState(false);

  const [productOption, setProductOptionType] =
    useState<ProductPurchaseOption>(defaultProductOption);

  const [product, setProduct] = useState<ProductPurchaseType>(defaultProduct);

  // Track router query parameters in state to avoid SSR issues
  const [queryParams, setQueryParams] = useState({
    affiliateCode: '',
    discount: '',
    referral: '',
  });
  const changeProduct = (product: ProductPurchaseType) => {
    setProduct(product);
  };

  const changeProductOption = (option: ProductPurchaseOption) => {
    setProductOptionType(option);
    setProduct(products[option]?.defaultProduct as ProductPurchaseType);
  };

  const price = products[productOption]?.plans[product]?.price ?? 0;

  // Calculate current product price for discount calculations
  const currentProductPrice =
    products[productOption]?.plans[product]?.price ?? 0;

  const showMedicalDisclaimer = false;

  const directCheckout = products[productOption]?.directCheckout ?? false;

  const getAffiliateParams = (productLink: string) => {
    return affiliateDetails
      ? `?affiliateCode=${affiliateDetails.code}${
          !campaignActive &&
          // store !== ShopifyStore.ROW &&
          affiliateDetails.discount_configs[store as ShopifyStore]?.M1?.code
            ? `&${
                productLink.includes('stripe')
                  ? 'prefilled_promo_code'
                  : 'discount'
              }=${
                affiliateDetails.discount_configs[store as ShopifyStore]?.M1
                  ?.code
              }`
            : ''
        }`
      : '';
  };

  const navigateToSubscriptionCheckout = async () => {
    const productData = products[productOption]?.plans[product];
    const productLink = productData?.checkoutUrl ?? '';
    setIsLoading(true);

    try {
      const params = new URLSearchParams(document.location.search);
      const metadata: Array<{ name: string; value: string }> = [];
      for (const param of params.entries()) {
        if (!['product', 'type'].includes(param[0])) {
          metadata.push({
            name: param[0],
            value: param[1],
          });
        }
      }

      const affiliateLink = getAffiliateParams(productLink);

      if (productLink && productLink.includes('stripe.com')) {
        await router.push(
          forwardSearchParams(
            forwardProductParams(productLink + affiliateLink),
            product,
          ),
        );
      }

      const effectiveStore = getEffectiveStoreForDiscount(store);

      let discountCode: string | undefined;

      if (affiliateDetails?.code === 'birthday') {
        discountCode =
          affiliateDetails?.discount_configs[effectiveStore]?.M1?.code;
      } else if (affiliateDetails?.discount_configs[effectiveStore]?.M1?.code) {
        discountCode =
          affiliateDetails.discount_configs[effectiveStore]?.M1?.code;
      } else if (typeof window !== 'undefined' && searchParams?.get('discount')) {
        discountCode = searchParams.get('discount') as string;
      }

      const bodyData = {
        checkout: {
          variant_id: productData?.variantId.split('/')[4],
          plan_id: productData?.planId,
          quantity: 1,
          currency: currency?.toUpperCase(),
          referral_discount:
            typeof window !== 'undefined' && searchParams?.get('referral')
              ? true
              : false,
          discount_code: discountCode || undefined,
          metadata: [
            ...metadata,
            { name: 'product_type', value: 'cyborg_subscription' },
            {
              name: 'subscription_purchase_from',
              value: 'pricing_page',
            },
          ],
        },
      };

      const response = await fetch(MEMBERSHIP_PRODUCT_API_PROD, {
        method: 'POST',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bodyData),
      });

      const data = await response.json();

      if (
        Object.keys(data).includes('data') &&
        Object.keys(data.data).includes('checkout') &&
        store
      ) {
        await router.push(
          forwardTrackingParams(
            data.data.checkout.url,
            productShopifyIdMap[searchParams?.get('product') as ProductPurchaseType]
              ?.productType as string,
            store,
          ),
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkout = async () => {
    if (!product || !productOption) return;

    setIsLoading(true);

    try {
      if (productOption === ProductPurchaseOption.membership) {
        if (
          cart &&
          cart?.lines &&
          cart?.lines.edges &&
          cart?.lines.edges.length >= 1
        ) {
          setAltContent(
            <SubscriptionDisclaimerDiv>
              <h3>Your plan can’t be added to this cart</h3>
              <p>
                Plans check out on their own. Continue with just the plan, or go
                back to your cart.
              </p>
              <div className="buttons">
                <ButtonV2
                  variant="blue"
                  disabled={isLoading}
                  onClick={async () => {
                    setAltContent(
                      <SubscriptionDisclaimerDiv>
                        <h3>Ordering your subscription.</h3>
                        <div className="loading-bar-subs"></div>
                      </SubscriptionDisclaimerDiv>,
                    );
                    await navigateToSubscriptionCheckout();
                  }}
                >
                  Complete checkout
                </ButtonV2>
                <ButtonV2
                  variant="transparentDark"
                  onClick={() => setAltContent(null)}
                >
                  Back to cart
                </ButtonV2>
              </div>
            </SubscriptionDisclaimerDiv>,
          );
          showCart();
        } else {
          await navigateToSubscriptionCheckout();
        }
        return;
      }

      if (products[productOption]?.directCheckout) {
        await navigateToSubscriptionCheckout();
        return;
      }

      const shopifyVariantId =
        products[productOption]?.plans[product]?.variantId;

      const lineItemsArray: Array<addToCartArgsType> = [];

      if (
        productOption === ProductPurchaseOption.x &&
        !ultrahumanXAdded &&
        isM1UhxBundlingEnabled(store)
      ) {
        lineItemsArray.push({
          shopifyVariantId: UhxShopifyMap[store as ShopifyStore].Year1
            .variantId as string,
          productType: ProductType.UHX,
        });
      }

      // Get M1 promo discount code - prioritize campaign discount over static config
      // This applies PER-PRODUCT: each M1 variant (2-weeks, 4-weeks, etc.) gets its own discount
      let m1DiscountCode: string | undefined;

      if (
        m1CampaignActive &&
        campaignConfig?.discountCode &&
        effectiveDiscount.type === 'campaign' &&
        (ultrahumanXAdded || !m1UhxBundlingEnabled)
      ) {
        m1DiscountCode = campaignConfig.discountCode;
      } else if (m1UhxBundlingEnabled && ultrahumanXAdded && store) {
        m1DiscountCode = ultrahumanXDiscountCodes[store]?.discountCode;
      } else if (
        store &&
        m1PromoDiscount[store]?.active &&
        m1PromoDiscount[store]?.discountCode
      ) {
        m1DiscountCode = m1PromoDiscount[store]?.discountCode;
      }

      lineItemsArray.push({
        shopifyVariantId,
        productType: ProductType.M1,
        discountCode: m1DiscountCode, // Per-product discount code
      });

      await addToCart(tagBundle(lineItemsArray));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const referralQuery = useMemo(
    () => (typeof window !== 'undefined' ? queryParams.referral : undefined),
    [queryParams.referral],
  );

  const affiliateDiscount = useMemo(() => {
    // Exclude affiliate discount when affiliateCode=birthday (like other products)
    if (
      typeof window !== 'undefined' &&
      queryParams.affiliateCode === 'birthday'
    ) {
      return 0;
    }

    const effectiveStore = getEffectiveStoreForDiscount(store);
    if (!effectiveStore) return 0;

    if (
      campaignActive ||
      !affiliateDetails ||
      !affiliateDetails.m1Active ||
      !affiliateDetails.discount_configs[effectiveStore]?.M1?.code ||
      referralQuery
    )
      return 0;

    if (workAffiliateDiscount) {
      return (currentProductPrice * workAffiliateDiscount.discount) / 100;
    }

    const discountConfig =
      affiliateDetails.discount_configs[effectiveStore]?.M1;

    return discountConfig?.type === 'percentage'
      ? (discountConfig.value * currentProductPrice) / 100
      : discountConfig?.value ?? 0;
  }, [
    campaignActive,
    affiliateDetails,
    currentProductPrice,
    workAffiliateDiscount,
    store,
    referralQuery,
  ]);

  // --- Birthday Discount ---
  // Use standardized discount_config approach like other products
  const birthdayDiscount = useMemo(() => {
    // Check if birthday discount is active via URL parameters
    const isBirthdayActive =
      typeof window !== 'undefined' &&
      queryParams.affiliateCode === 'birthday' &&
      queryParams.discount;

    if (!isBirthdayActive) return 0;

    // Extract percentage from discount code (last 2 characters if they're numbers)
    const discountCode = queryParams.discount;
    const lastTwoChars = discountCode.slice(-2);
    const percentage = parseInt(lastTwoChars);
    const discountPercentage =
      !isNaN(percentage) && percentage > 0 && percentage <= 100
        ? percentage
        : 15;

    return currentProductPrice * (discountPercentage / 100);
  }, [currentProductPrice, queryParams.affiliateCode, queryParams.discount]);

  // --- Referral Discount ---
  const referralDiscount = useMemo(
    () =>
      referralQuery
        ? currentProductPrice * 0.1 // 10% referral discount
        : 0,
    [referralQuery, currentProductPrice],
  );

  // --- Campaign Discount ---
  const m1UhxBundlingEnabled = isM1UhxBundlingEnabled(store);

  const m1Campaign = getCampaignForProduct('cgm');
  const campaignConfig = getCampaignConfig(m1Campaign);
  const m1CampaignActive = campaignConfig?.active ?? false;
  const campaignTitle = m1Campaign?.title;

  const campaignDiscount = useMemo(() => {
    if (productOption !== ProductPurchaseOption.oneTimePurchase) {
      return 0;
    }

    const promoConfig = getCampaignPromoDiscountConfig({
      store,
      country: locale,
      product: 'm1',
      campaignConfig,
    });

    if (!promoConfig?.active || !promoConfig?.discount) {
      return 0;
    }

    const discountValue = promoConfig.discount;
    const discountType = promoConfig.discountType;

    return discountType === 'percentage'
      ? currentProductPrice * (discountValue / 100)
      : discountValue;
  }, [store, locale, currentProductPrice, campaignConfig, productOption]);

  const uhxDiscount = useMemo(() => {
    if (
      !m1UhxBundlingEnabled ||
      !ultrahumanXAdded ||
      !store ||
      productOption !== ProductPurchaseOption.oneTimePurchase
    ) {
      return 0;
    }

    const uhxConfig = ultrahumanXDiscountCodes[store as ShopifyStore];
    if (!uhxConfig?.discount) return 0;

    return currentProductPrice * uhxConfig.discount;
  }, [
    ultrahumanXAdded,
    store,
    currentProductPrice,
    productOption,
    m1UhxBundlingEnabled,
  ]);

  // Calculate all discounts and find the highest one
  const discounts = useMemo(
    () => [
      { type: 'referral', amount: referralDiscount },
      { type: 'affiliate', amount: affiliateDiscount },
      { type: 'birthday', amount: birthdayDiscount },
      { type: 'campaign', amount: campaignDiscount },
      { type: 'uhx', amount: uhxDiscount },
    ],
    [
      referralDiscount,
      affiliateDiscount,
      birthdayDiscount,
      campaignDiscount,
      uhxDiscount,
    ],
  );

  const effectiveDiscount = useMemo(
    () =>
      discounts.reduce(
        (max, current) => (current.amount > max.amount ? current : max),
        { type: 'affiliate', amount: 0 },
      ),
    [discounts],
  );

  const effectivePrice = currentProductPrice - effectiveDiscount.amount;

  useEffect(() => {
    if (typeof window !== 'undefined' && router) {
      const newQueryParams = {
        affiliateCode: searchParams?.get('affiliateCode') || '',
        discount: searchParams?.get('discount') || '',
        referral: searchParams?.get('referral') || '',
      };

      // Only update if the parameters actually changed
      if (
        newQueryParams.affiliateCode !== queryParams.affiliateCode ||
        newQueryParams.discount !== queryParams.discount ||
        newQueryParams.referral !== queryParams.referral
      ) {
        setQueryParams(newQueryParams);
      }
    }
  });

  useEffect(() => {
    if (!isCartVisible) setIsLoading(false);
  }, [isCartVisible]);

  useEffect(() => {
    if (mcfDeliveryEstimateState) {
      setShippingEstimate('Delivery in 3-5 days');
      return;
    }
    if (store === ShopifyStore.IN) {
      setShippingEstimate('Free shipping: Delivery in 1-2 days');
    } else if (store === ShopifyStore.US) {
      setShippingEstimate('Delivery in 3-5 days');
    } else if (store === ShopifyStore.AE) {
      setShippingEstimate('Free shipping: Delivery in 3-4 days');
    } else if (store === ShopifyStore.GB) {
      setShippingEstimate('Delivery in 3-5 days');
    } else {
      setShippingEstimate('Free shipping: Delivery in 3-5 days');
    }
  }, [store]);

  // useEffect(() => {
  //   setMcfDeliveryEstimate(false);
  // }, [router.asPath]);

  return (
    <M1SelectionContext.Provider
      value={{
        currency,
        store,
        locale,
        productOption,
        changeProductOption,
        product,
        changeProduct,
        price,
        checkout,
        products,
        showMedicalDisclaimer,
        directCheckout,
        isLoading,
        shippingEstimate,
        effectivePrice,
        outOfStock,
        mcfDeliveryEstimateState,
        setMcfDeliveryEstimateState,
        affiliateDiscount,
        birthdayDiscount,
        referralDiscount,
        campaignDiscount,
        effectiveDiscount,
        m1CampaignActive,
        campaignTitle,
      }}
    >
      {children}
    </M1SelectionContext.Provider>
  );
};
